import React from 'react';
import { Button, Row, Col, Form, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import SettingsCard from '../SettingsCard';
import { BookCheck, Bot, CheckCircle, Info, Mail, RefreshCw, Save, ScanEye, Send, Sparkles, Zap } from 'lucide-react';

// Leverantörsfaktura-agentens inställningar — approver/notification channel and the
// auto-approval rule thresholds. State, fetch and save live in the
// CompanySettings shell (lazy-loaded on first visit) and arrive as props.
const AgentsSection = ({
  agentSettings,
  setAgentSettings,
  saveAgentSettings,
  agentSettingsLoading,
  agentSettingsSaving,
}) => {
  if (agentSettingsLoading) {
    return (
      <div className="text-center py-5">
        <RefreshCw className="spin" size={24} />
        <p className="text-muted mt-2">Laddar inställningar...</p>
      </div>
    );
  }

  return (
    <>
      {/* Was a gradient hero with a 52px blue tile and a 1.35rem title —
          the only page in Inställningar that opened with a banner instead of
          a card header, and the rail already says AI-agenter. Now the same
          card header as everywhere else, keeping the agent's name because
          naming what is configured rather than who does it. */}
      <SettingsCard
        icon={Bot}
        title="Leverantörsfakturor"
        subtitle="Hur inkommande leverantörsfakturor tas emot, tolkas och bokförs. Paige gör arbetet — du sätter reglerna."
        actions={(
          <Button variant="primary" onClick={saveAgentSettings} disabled={agentSettingsSaving}>
            <Save className="me-2" size={16} />
            {agentSettingsSaving ? 'Sparar…' : 'Spara'}
          </Button>
        )}
      >
        {/* One card, three sections — the Företagsuppgifter shape. The Spara
            in the header saves all of this, and before the fold it lived in a
            different card from the fields it writes. Sections also give the
            next agent setting somewhere to go without adding another card. */}
        <div className="company-form-grid">
          <div className="form-section-wrapper">
            <div className="section-header-external">
              <div className="section-icon"><Sparkles size={16} /></div>
              <div>
                <h3 className="section-heading-external">Hur det fungerar</h3>
              </div>
            </div>

            <Row>
            {[
            { icon: <Mail size={16} />, step: '1', title: 'Ta emot', desc: 'Vidarebefordra fakturor via e-post till din unika adress' },
            { icon: <ScanEye size={16} />, step: '2', title: 'Tolka', desc: 'Paige läser av fakturan med OCR och AI' },
            { icon: <CheckCircle size={16} />, step: '3', title: 'Granska', desc: 'Du godkänner eller justerar bokföringsförslaget' },
            { icon: <BookCheck size={16} />, step: '4', title: 'Bokför', desc: 'Verifikatet skapas automatiskt i din bokföring' },
            ].map(({ icon, step, title, desc }) => (
            <Col xs={6} md={3} key={step} className="text-center mb-3 mb-md-0">
            <div className="agent-step__icon">{icon}</div>
            <div className="agent-step__title">{title}</div>
            <div className="agent-step__desc">{desc}</div>
            </Col>
            ))}
            </Row>
          </div>

          <div className="form-section-wrapper" id="godkannare">
            <div className="section-header-external">
              <div className="section-icon"><Mail size={16} /></div>
              <div>
                <h3 className="section-heading-external">Godkännare & notifikationer</h3>
                <p className="section-description">Vem som får fakturor för manuellt godkännande, och hur.</p>
              </div>
            </div>


            <Form.Group className="mb-4">
              <div className="d-flex align-items-center gap-1">
                <span className="modern-label mb-0">E-post för godkännande</span>
                <OverlayTrigger placement="top" overlay={<Tooltip>Hit skickas fakturor som behöver manuellt godkännande</Tooltip>}>
                  <Info size={14} style={{ color: '#9ca3af', cursor: 'help' }} />
                </OverlayTrigger>
              </div>
              <input
                type="email"
                className="modern-input mt-1"
                value={agentSettings.approverEmail}
                onChange={(e) => setAgentSettings(prev => ({ ...prev, approverEmail: e.target.value }))}
                placeholder="namn@foretag.se"
              />
            </Form.Group>

            <Form.Group>
              <span className="modern-label">Notifikationskanal</span>
              <div className="d-flex flex-column gap-2">
                {[
                  { value: 'email', label: 'E-post', icon: <Mail size={16} />, disabled: false },
                  { value: 'slack', label: 'Slack', icon: <Send size={16} />, disabled: true, badge: 'Kommer snart' },
                  { value: 'teams', label: 'Microsoft Teams', icon: <Send size={16} />, disabled: true, badge: 'Kommer snart' },
                ].map(opt => (
                  <div
                    key={opt.value}
                    onClick={() => !opt.disabled && setAgentSettings(prev => ({ ...prev, approvalChannel: opt.value }))}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '10px 14px', borderRadius: 8,
                      border: `1px solid ${agentSettings.approvalChannel === opt.value ? 'var(--primary)' : '#e5e7eb'}`,
                      background: agentSettings.approvalChannel === opt.value
                        ? 'var(--rgba-primary-1)'
                        : opt.disabled ? '#fafafa' : '#fff',
                      cursor: opt.disabled ? 'default' : 'pointer',
                      opacity: opt.disabled ? 0.55 : 1,
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <span style={{ color: agentSettings.approvalChannel === opt.value ? 'var(--primary)' : '#9ca3af' }}>{opt.icon}</span>
                    <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>{opt.label}</span>
                    {opt.badge && (
                      <Badge bg="light" text="muted" style={{ fontSize: '0.7rem', marginLeft: 'auto' }}>{opt.badge}</Badge>
                    )}
                    {agentSettings.approvalChannel === opt.value && !opt.disabled && (
                      <CheckCircle size={16} style={{ marginLeft: 'auto', color: 'var(--primary)' }} />
                    )}
                  </div>
                ))}
              </div>
            </Form.Group>
          </div>

          <div className="form-section-wrapper" id="auto-godkannande">
            <div className="section-header-external">
              <div className="section-icon"><Zap size={16} /></div>
              <div>
                <h3 className="section-heading-external">Automatiskt godkännande</h3>
                <p className="section-description">Fakturor som uppfyller alla villkor nedan godkänns utan manuell granskning.</p>
              </div>
            </div>


            <Form.Group className="mb-4">
              <div className="d-flex align-items-center justify-content-between mb-1">
                <div className="d-flex align-items-center gap-1">
                  <span className="modern-label mb-0">Maxbelopp</span>
                  <OverlayTrigger placement="top" overlay={<Tooltip>Fakturor över detta belopp kräver alltid manuellt godkännande. Sätt till 0 för att inaktivera.</Tooltip>}>
                    <Info size={14} style={{ color: '#9ca3af', cursor: 'help' }} />
                  </OverlayTrigger>
                </div>
                <span style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--primary)' }}>
                  {Number(agentSettings.autoApproveMaxAmountSek).toLocaleString('sv-SE')} kr
                </span>
              </div>
              <input
                type="number"
                className="modern-input"
                min="0"
                step="500"
                value={agentSettings.autoApproveMaxAmountSek}
                onChange={(e) => setAgentSettings(prev => ({ ...prev, autoApproveMaxAmountSek: Number(e.target.value) }))}
              />
            </Form.Group>

            <Form.Group className="mb-4">
              <div className="d-flex align-items-center justify-content-between mb-1">
                <div className="d-flex align-items-center gap-1">
                  <span className="modern-label mb-0">Minsta OCR-konfidensgrad</span>
                  <OverlayTrigger placement="top" overlay={<Tooltip>Fakturan måste ha minst denna konfidensgrad från OCR-tolkningen för att godkännas automatiskt</Tooltip>}>
                    <Info size={14} style={{ color: '#9ca3af', cursor: 'help' }} />
                  </OverlayTrigger>
                </div>
                <Badge
                  bg={agentSettings.autoApproveMinConfidence >= 0.9 ? 'success' : agentSettings.autoApproveMinConfidence >= 0.7 ? 'warning' : 'danger'}
                  style={{ minWidth: '52px', fontSize: '0.8rem', fontWeight: 600 }}
                >
                  {Math.round(agentSettings.autoApproveMinConfidence * 100)}%
                </Badge>
              </div>
              <Form.Range
                min={0}
                max={1}
                step={0.05}
                value={agentSettings.autoApproveMinConfidence}
                onChange={(e) => setAgentSettings(prev => ({ ...prev, autoApproveMinConfidence: Number(e.target.value) }))}
              />
              <div className="d-flex justify-content-between" style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: -4 }}>
                <span>Låg</span>
                <span>Hög</span>
              </div>
            </Form.Group>

            <div style={{
              padding: '14px 16px', borderRadius: 10,
              background: '#f9fafb', border: '1px solid #f3f4f6'
            }}>
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2">
                  <div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 500 }}>Kräv känd leverantör</div>
                  </div>
                  <OverlayTrigger placement="top" overlay={<Tooltip>Okända leverantörer skickas alltid till manuell granskning</Tooltip>}>
                    <Info size={14} style={{ color: '#9ca3af', cursor: 'help' }} />
                  </OverlayTrigger>
                </div>
                <Form.Check
                  type="switch"
                  id="auto-approve-known-supplier"
                  checked={agentSettings.autoApproveKnownSupplierOnly}
                  onChange={(e) => setAgentSettings(prev => ({ ...prev, autoApproveKnownSupplierOnly: e.target.checked }))}
                />
              </div>
            </div>
          </div>
        </div>
      </SettingsCard>
    </>
  );
};

export default AgentsSection;
